import { computed, ref, readonly } from 'vue'
import type { ComputedRef, DeepReadonly, Ref } from 'vue'
import { useViewManager } from '../view-manager/useViewManager'
import { WidgetViews } from '@/types'
import type { Message } from '@/composables/session/websocket/frames'
import { viewsKeys, widgetViews } from './views'
import type { View } from '../view-manager/types'
import type { Track } from '../tracker/useTracker'
import { useI18n } from '../i18n/useI18n'
import useWidgetOptions from '../widget-options/useWidgetOptions'
import * as Sentry from '@sentry/vue'
import useWidgetHooks from '../widget-hooks/useWidgetHooks'
import useEmbed from '@/embed/code'
import useWidgetSession, { useSessionContext } from '@/composables/session/useWidgetSession'
import type { ViewState } from '@/composables/session/view-state'

export { getWorkflowExecutionPhase } from '@/composables/session/websocket/strategies/view-state'

const {
    changeView,
    currentView,
    currentViewOptions,
} = useViewManager(viewsKeys)

const lastInnerViewOfPreviousView = ref<View>(null)

const { options: widgetOptions } = useWidgetOptions()

const activeViewState = ref<ViewState | null>(null)

const activeMessage = computed(() => activeViewState.value?.raw || null)

const activeViewRef = computed(() => activeViewState.value?.ref)

function useWidgetViewManager<M extends Message = Message>() {
    const watchWorkflowEnd = () => {
        useWidgetSession().strategy?.watchWorkflowEnd(reason => {
            if (reason === 'timeout') {
                showTimeoutError()
            }

            if (reason === 'cancelled') {
                showCancelledWorkflowError()
            }
        })
    }

    const initialize = () => {
        changeView(WidgetViews.LOADING, { hideBack: true })
    }

    const showFirstView = (state: ViewState) => {
        const { t } = useI18n()

        setViewState(state)

        if (state.terminal) {
            if (state.view) {
                return changeView(state.view)
            }

            return showError({
                title: t('views.widget.initialization_failed.title'),
                description: t('views.widget.initialization_failed.description'),
                retry: true,
                svgIcon: 'bug.svg',
            })
        }

        watchWorkflowEnd()

        if (state.view) {
            return changeView(state.view)
        }

        return changeView(WidgetViews.ERROR, {
            errorDetails: 'No app view found for the message',
        })
    }

    const setViewState = (state: ViewState) => {
        activeViewState.value = state

        const { context } = state

        Sentry.setTags({
            task_id: context.taskId,
            task_definition_id: context.taskDefinitionId,
            task_name: context.taskName,
            task_type: context.taskType,
            workflow_id: context.workflowId,
            workspace_id: context.workspaceId,
        })

        useEmbed().emitEventToApi('view-context-changed', context)
    }

    const setLastInnerView = (value: View) => {
        lastInnerViewOfPreviousView.value = value
    }

    const getAppViewMessages = computed<Message[]>(() => {
        return useWidgetSession().strategy?.currentTaskEvents() ?? []
    })

    const finishAppView = async (options: {
        track?: Track
        loader?: boolean
    } = {
        loader: true,
    }) => {
        if (options.track) {
            options.track('screenView', 'verificationSuccess')
        }

        if (widgetOptions.value.loading && options.loader) {
            await changeView(WidgetViews.PROCESSING, { hideBack: true })
        }

        const nextView = await useWidgetSession().strategy!.advance(
            useSessionContext(),
            activeViewState.value!.ref,
        )

        if (nextView.phase) {
            useEmbed().setWorkflowExecutionPhase(nextView.phase)
        }

        setViewState(nextView)

        await changeView(nextView.view || null)

        Sentry.logger.trace(`View manager changes app to ${nextView.view}`)

        return true
    }

    const currentViewComponent = computed(() => widgetViews[currentView.value as keyof typeof widgetViews])

    const resetView = () => {
        activeViewState.value = null
        changeView(WidgetViews.LOADING)
    }

    const goToPreviousView = () => {
        changeView(currentViewOptions.value.previousView)
    }

    type ShowErrorOptions = {
        title?: string
        description?: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error?: any
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorDetails?: any
        retry?: boolean
        close?: boolean
        svgIcon?: string
    }

    const showError = (options: ShowErrorOptions) => {
        const { widgetOpened, setWidgetOpened } = useWidgetHooks()

        if (!widgetOpened.value) {
            setWidgetOpened(true)
        }

        const error = options.error?.response?.data || options.error

        useEmbed().setWorkflowExecutionPhase('failed')

        const errorMessage = Array.isArray(error?.errors)
            ? error?.errors.map((err: Record<string, string>) => err.message || err.code || err.outputMessage).filter(Boolean).join(', ')
            : error?.message || error?.code || error?.outputMessage

        return changeView(WidgetViews.ERROR, {
            ...options,
            error: errorMessage,
            errorDetails: error || options.title,
        })
    }

    const showTimeoutError = (options?: Partial<ShowErrorOptions>) => {
        const { t } = useI18n()
        return showError({
            title: t('views.widget.workflow_timeout.title'),
            description: t('views.widget.workflow_timeout.description'),
            svgIcon: 'timeout.svg',
            retry: false,
            ...options || {},
        })
    }

    const showCancelledWorkflowError = (options?: Partial<ShowErrorOptions>) => {
        const { t } = useI18n()
        showError({
            title: t('views.widget.workflow_cancelled.title'),
            description: t('views.widget.workflow_cancelled.description'),
            svgIcon: 'timeout.svg',
            retry: false,
            close: true,
            ...options || {},
        })
    }

    return {
        initialize,
        changeView,
        currentView,
        currentViewComponent,
        currentViewOptions,
        lastInnerViewOfPreviousView: readonly(lastInnerViewOfPreviousView),
        setLastInnerView,
        finishAppView,
        getAppViewMessages: getAppViewMessages as ComputedRef<M[]>,
        setViewState,
        showFirstView,
        activeMessage: activeMessage as DeepReadonly<Ref<M | null>>,
        activeViewRef,
        resetView,
        goToPreviousView,
        showError,
        showTimeoutError,
    }
}

export default useWidgetViewManager
